import React from "react";
import PropTypes from "prop-types";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const lmUrl =
  "http://splitgal4.janelia.org/cgi-bin/view_splitgal4_imagery.cgi?slidecode=<NAME>";
const lmrawUrl =
  "https://flylight-raw.janelia.org/cgi-bin/view_raw_imagery.cgi?slidecode=<NAME>"
const mcfoUrl =
  "http://gen1mcfo.janelia.org/cgi-bin/view_gen1mcfo_imagery.cgi?slidecode=<NAME>";
// https://neuprint.janelia.org/view?dataset=hemibrain:v1.1&bodyid=12345678
const vfbUrl = "http://virtualflybrain.org/xref/neuronbridge/<NAME>";
const flyWireUrl =
    "https://codex.flywire.ai/app/cell_details?dataset=<DATASET>&data_version=<VERSION>&root_id=<ID>";

function VFBLink({ name }) {
  return (
    <a href={vfbUrl.replace(/<NAME>/, name)} target="_blank" rel="noopener noreferrer">
      Virtual Fly Brain{" "}
      <FontAwesomeIcon icon={faExternalLink} size="xs" transform="up-2" />
    </a>
  );
}

VFBLink.propTypes = {
  name: PropTypes.string.isRequired,
};

function FlyWireLink({ tag, dataset }) {
  const [, version, id] = tag.split(":");

  return (
    <a
      href={flyWireUrl
        .replace(/<ID>/, id)
        .replace(/<VERSION>/, version.replace(/^v/, ""))
        .replace(/<DATASET>/, dataset)}
      target="_blank"
      rel="noopener noreferrer"
    >
      FlyWire{" "}
      <FontAwesomeIcon icon={faExternalLink} size="xs" transform="up-2" />
    </a>
  );
}

FlyWireLink.propTypes = {
  tag: PropTypes.string.isRequired,
  dataset: PropTypes.string,
};

FlyWireLink.defaultProps = {
  dataset: "fafb",
};

export default function ExternalLink({ id, isLM, library, publishedName }) {
  if (isLM) {
    let extUrl = lmUrl;
    let extName = "FlyLight Split-GAL4";

    if (library.match(/gen1.*mcfo/i)) {
      extUrl = mcfoUrl;
      extName = "FlyLight Gen1 MCFO";
    } else if (library.match(/Omnibus Broad/i)) {
      extUrl = lmrawUrl;
      extName = "FlyLight Raw Collection";
    }

    return (
      <>
        <a href={extUrl.replace(/<NAME>/, id)} target="_blank" rel="noopener noreferrer">
          {extName}{" "}
          <FontAwesomeIcon icon={faExternalLink} size="xs" transform="up-2" />
        </a>
        {process.env.REACT_APP_LEVEL &&
        process.env.REACT_APP_LEVEL.match(/pre$/) ? (
          ""
        ) : (
          <>
            <br />
            <VFBLink name={publishedName} />
          </>
        )}
      </>
    );
  }

  // This is an EM library, so process with alternative logic

  // The ids are in the format of library:vX:idnumber, eg: male-cns:v0.9:17253
  // We need to extract the dataset from the bodyId to construct the neuprint link
  // eg: id = male-cns:v0.9:17253 -> dataset = male-cns:v0.9 ; bodyId = 17253
  // This can be accomplished by splitting on the second colon and using the first part as
  // the dataset and the last part as the bodyId
  let dataset;
  let bodyId;

  const match = id.match(/^([^:]*:[^:]*):(.*)$/);
  if (!match) { // Fallback if the id doesn't match the expected fully qualified id format
    dataset = library
      .replace(/flyem_/i, "")
      .toLowerCase()
      .replace("_v", ":v")
      .replace(/\s+/g, "_");
    bodyId = id.split(":").pop();
  } else {
    [, dataset, bodyId] = match;
  }

   // TODO: fix the pre-release site dataset since it shouldn't be missing the
  // version number, but the pre-release site doesn't have a version number.
  // Also, the pre-release data on neuprint was called vnc and not manc, so the
  // dataset name needs to be converted as well.
  if (
    process.env.REACT_APP_LEVEL &&
    process.env.REACT_APP_LEVEL.match(/pre$/)
  ) {
    dataset = dataset.split(":").shift();
    if (dataset === "manc") {
      dataset = "vnc";
    }
  }

  const emUrl =
    process.env.REACT_APP_LEVEL && process.env.REACT_APP_LEVEL.match(/pre$/)
      ? "https://neuprint-pre.janelia.org/view?dataset=<DATASET>&bodyid=<NAME>"
      : "https://neuprint.janelia.org/view?dataset=<DATASET>&bodyid=<NAME>";

  const finalEMUrl = emUrl
    .replace(/<NAME>/, bodyId)
    .replace(/<DATASET>/, dataset);

  const isFlywireFafbLibrary = Boolean(library.match(/flywire_fafb/i));
  const isFlywireBancLibrary = Boolean(library.match(/flywire_banc/i));
  const isFlywireLibrary = Boolean(library.match(/flywire/i));

  let secondaryLink = <VFBLink name={id} />;
  if (isFlywireFafbLibrary) {
    secondaryLink = (
      <>
        <FlyWireLink tag={id} />
        <br/>
        <VFBLink name={id} />
      </>
    );
  } else if (isFlywireBancLibrary) {
    secondaryLink = (
      <>
        <FlyWireLink tag={id} dataset="banc" />
        <br/>
        <VFBLink name={id} />
      </>
    );
  }

  return (
    <>
      { !isFlywireLibrary ? (
        <>
      <a href={finalEMUrl} target="_blank" rel="noopener noreferrer">
        NeuPrint{" "}
        <FontAwesomeIcon icon={faExternalLink} size="xs" transform="up-2" />
        </a><br /></>) : ""}
      {process.env.REACT_APP_LEVEL &&
      process.env.REACT_APP_LEVEL.match(/pre$/) ? (
        ""
      ) : (
        secondaryLink
      )}
    </>
  );
}

ExternalLink.propTypes = {
  id: PropTypes.string.isRequired,
  publishedName: PropTypes.string,
  library: PropTypes.string,
  isLM: PropTypes.bool,
};

ExternalLink.defaultProps = {
  isLM: true,
  publishedName: "",
  library: "flylight_splitgal4_drivers",
};
